import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  Calendar, 
  Settings, 
  Trash2, 
  Edit, 
  Eye, 
  EyeOff, 
  Plus, 
  X, 
  Download, 
  Upload, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  UserCheck, 
  UserX, 
  Loader,
  Search,
  Filter,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MessageSquare,
  Bot,
  Server,
  FileText,
  BarChart3,
  Shield,
  Crown,
  UserPlus,
  MessageCircle,
  Users as UsersIcon,
  Calendar as CalendarIcon,
  Settings as SettingsIcon,
  Trash2 as Trash2Icon,
  Edit as EditIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Plus as PlusIcon,
  X as XIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  AlertTriangle as AlertTriangleIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  UserCheck as UserCheckIcon,
  UserX as UserXIcon,
  Loader as LoaderIcon,
  Search as SearchIcon,
  Filter as FilterIcon,
  MoreHorizontal as MoreHorizontalIcon,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  ExternalLink as ExternalLinkIcon,
  MessageSquare as MessageSquareIcon,
  Bot as BotIcon,
  Server as ServerIcon,
  FileText as FileTextIcon,
  BarChart3 as BarChart3Icon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ENABLE_TEST_ENVIRONMENT } from '../config';
import { userInputToUTC } from '../utils/timezone';

// Conditionally import TestEnvironment component
let TestEnvironment = null;
if (ENABLE_TEST_ENVIRONMENT) {
  try {
    TestEnvironment = require('../components/TestEnvironment').default;
  } catch (error) {
    console.warn('Failed to load TestEnvironment component:', error.message);
  }
}

const Admin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState({
    totalUsers: 0,
    totalEvents: 0,
    totalSignups: 0,
    recentEvents: [],
    recentUsers: []
  });
  const [siteSettings, setSiteSettings] = useState({
    events_banner: '',
    discord_event_image: ''
  });
  const [editingSettings, setEditingSettings] = useState({});
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [serverNames, setServerNames] = useState({});
  const [serverNamesLoading, setServerNamesLoading] = useState(false);
  const [preferredUsernamesLoading, setPreferredUsernamesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEventManagement, setShowEventManagement] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [deleteDiscordEvents, setDeleteDiscordEvents] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [eventStatusFilter, setEventStatusFilter] = useState('');
  const [showSignupsModal, setShowSignupsModal] = useState(false);
  const [signups, setSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupSearchTerm, setSignupSearchTerm] = useState('');
  const [signupStatusFilter, setSignupStatusFilter] = useState('');
  const [signupSourceFilter, setSignupSourceFilter] = useState('');
  const [signupEventFilter, setSignupEventFilter] = useState('');
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedSignups, setSelectedSignups] = useState([]);
  const [editingSignup, setEditingSignup] = useState(null);
  const [editingSignupData, setEditingSignupData] = useState({});
  const [showDiscordImport, setShowDiscordImport] = useState(false);
  const [discordEvents, setDiscordEvents] = useState([]);
  const [discordEventsLoading, setDiscordEventsLoading] = useState(false);
  const [importingEvent, setImportingEvent] = useState(null);
  const [importProgress, setImportProgress] = useState({});
  const [showTemplateEvents, setShowTemplateEvents] = useState(false);
  const [templateEventsLoading, setTemplateEventsLoading] = useState(false);
  const [storytellers, setStorytellers] = useState([]);
  const [storytellerSearch, setStorytellerSearch] = useState('');
  const [showStorytellerDropdown, setShowStorytellerDropdown] = useState(null);
  const [templateConfig, setTemplateConfig] = useState({
    startDate: '',
    storytellerIds: [],
    location: '',
    description: 'The clocktower tolls, shadows stir, and whispers weave through the village…\nJoin us for an evening of deception, deduction, and dramatic executions in Blood on the Clocktower.',
    createDiscordEvents: true,
    events: [
      { day: 1, name: 'Monday', time: '20:30', enabled: false, storytellerIds: [] },
      { day: 2, name: 'Tuesday', time: '20:30', enabled: false, storytellerIds: [] },
      { day: 3, name: 'Wednesday', time: '20:30', enabled: true, storytellerIds: [] },
      { day: 4, name: 'Thursday', time: '20:30', enabled: false, storytellerIds: [] },
      { day: 5, name: 'Friday', time: '20:30', enabled: true, storytellerIds: [] },
      { day: 6, name: 'Saturday', time: '20:30', enabled: true, storytellerIds: [] },
      { day: 0, name: 'Sunday', time: '20:30', enabled: false, storytellerIds: [] },
    ]
  });


  useEffect(() => {
    fetchAdminData();
  }, []);

  // Load users when user management modal opens
  useEffect(() => {
    if (showUserManagement) {
      fetchUsers();
    }
  }, [showUserManagement]);

  // Load events when event management modal opens
  useEffect(() => {
    if (showEventManagement) {
      fetchEvents();
    }
  }, [showEventManagement]);

  // Load signups and events when signups modal opens
  useEffect(() => {
    if (showSignupsModal) {
      fetchSignups();
      fetchAvailableEvents();
    }
  }, [showSignupsModal]);

  // Load Discord events when Discord import modal opens
  useEffect(() => {
    if (showDiscordImport) {
      fetchDiscordEvents();
    }
  }, [showDiscordImport]);

  // Load storytellers when template events modal opens
  useEffect(() => {
    if (showTemplateEvents) {
      fetchStorytellers();
      setDefaultStartDate();
    }
  }, [showTemplateEvents]);

  // Close storyteller dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showStorytellerDropdown !== null) {
        const dropdown = event.target.closest('.storyteller-dropdown');
        if (!dropdown) {
          handleCloseStorytellerDropdown();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStorytellerDropdown]);



  const fetchAdminData = async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([
        axios.get('/api/admin/stats'),
        axios.get('/api/admin/settings')
      ]);

      setAdminData(statsRes.data);
      setSiteSettings(settingsRes.data);
      setEditingSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingUpdate = async (key, value) => {
    try {
      await axios.put(`/api/admin/settings/${key}`, { value });
      setSiteSettings(prev => ({ ...prev, [key]: value }));
      alert('Setting updated successfully!');
    } catch (error) {
      console.error('Failed to update setting:', error);
      alert('Failed to update setting');
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      alert('Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchServerNames = async () => {
    setServerNamesLoading(true);
    try {
      const response = await axios.get('/api/admin/users/server-names');
      setServerNames(response.data);
    } catch (error) {
      console.error('Failed to fetch server names:', error);
      // Don't show alert for server names failure as it's not critical
    } finally {
      setServerNamesLoading(false);
    }
  };

  const updatePreferredUsernames = async () => {
    setPreferredUsernamesLoading(true);
    try {
      const response = await axios.post('/api/admin/users/update-preferred-usernames');
      alert(`✅ ${response.data.message}\n\nUpdated: ${response.data.updated} out of ${response.data.total} users`);
      
      // Refresh users list to show updated preferred usernames
      await fetchUsers();
    } catch (error) {
      console.error('Failed to update preferred usernames:', error);
      alert('❌ Failed to update preferred usernames: ' + (error.response?.data?.error || error.message));
    } finally {
      setPreferredUsernamesLoading(false);
    }
  };

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const response = await axios.get('/api/admin/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      alert('Failed to fetch events');
    } finally {
      setEventsLoading(false);
    }
  };

  const handleDeleteEvents = async () => {
    if (selectedEvents.length === 0) {
      alert('Please select events to delete');
      return;
    }

    // Check if any selected events have Discord events
    const selectedEventData = events.filter(event => selectedEvents.includes(event.id));
    const eventsWithDiscord = selectedEventData.filter(event => event.discord_event_id);
    
    // Set default Discord deletion state based on whether any events have Discord events
    setDeleteDiscordEvents(eventsWithDiscord.length > 0);
    setShowDeleteModal(true);
  };

  const confirmDeleteEvents = async () => {
    try {
      const response = await axios.post('/api/admin/delete-events', { 
        eventIds: selectedEvents,
        deleteDiscordEvents 
      });
      
      setSelectedEvents([]);
      setShowDeleteModal(false);
      fetchEvents();
      fetchAdminData(); // Refresh admin stats
      
      const message = `Events deleted successfully!${response.data.discordEventsDeleted > 0 ? ` ${response.data.discordEventsDeleted} Discord events were also deleted.` : ''}`;
      alert(message);
    } catch (error) {
      console.error('Failed to delete events:', error);
      alert('Failed to delete events: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSelectAllEvents = () => {
    if (selectedEvents.length === events.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(events.map(event => event.id));
    }
  };

  const handleSelectEvent = (eventId) => {
    if (selectedEvents.includes(eventId)) {
      setSelectedEvents(selectedEvents.filter(id => id !== eventId));
    } else {
      setSelectedEvents([...selectedEvents, eventId]);
    }
  };

  const handleUserRoleUpdate = async (userId, newRole) => {
    try {
      await axios.put(`/api/admin/users/${userId}/role`, { role: newRole });
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      setEditingUser(null);
      alert('User role updated successfully!');
    } catch (error) {
      console.error('Failed to update user role:', error);
      alert('Failed to update user role: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/users/${userId}`);
      setUsers(users.filter(user => user.id !== userId));
      alert('User deleted successfully!');
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchSignups = async () => {
    setSignupsLoading(true);
    try {
      const response = await axios.get('/api/admin/signups');
      setSignups(response.data);
    } catch (error) {
      console.error('Failed to fetch signups:', error);
      alert('Failed to fetch signups');
    } finally {
      setSignupsLoading(false);
    }
  };

  const fetchAvailableEvents = async () => {
    try {
      const response = await axios.get('/api/admin/events');
      setAvailableEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events for filter:', error);
    }
  };

  const handleSelectAllSignups = () => {
    if (selectedSignups.length === filteredSignups.length) {
      setSelectedSignups([]);
    } else {
      setSelectedSignups(filteredSignups.map(signup => signup.id));
    }
  };

  const handleSelectSignup = (signupId) => {
    if (selectedSignups.includes(signupId)) {
      setSelectedSignups(selectedSignups.filter(id => id !== signupId));
    } else {
      setSelectedSignups([...selectedSignups, signupId]);
    }
  };

  const handleDeleteSelectedSignups = async () => {
    if (selectedSignups.length === 0) {
      alert('Please select signups to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedSignups.length} selected signups? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.post('/api/admin/delete-signups', { signupIds: selectedSignups });
      setSelectedSignups([]);
      fetchSignups();
      fetchAdminData(); // Refresh admin stats
      alert('Signups deleted successfully!');
    } catch (error) {
      console.error('Failed to delete signups:', error);
      alert('Failed to delete signups: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditSignup = (signup) => {
    setEditingSignup(signup.id);
    setEditingSignupData({
      status: signup.status,
      notes: signup.notes || ''
    });
  };

  const handleUpdateSignup = async (signupId) => {
    try {
      await axios.put(`/api/admin/signups/${signupId}`, editingSignupData);
      setSignups(signups.map(signup => 
        signup.id === signupId 
          ? { ...signup, ...editingSignupData }
          : signup
      ));
      setEditingSignup(null);
      setEditingSignupData({});
      alert('Signup updated successfully!');
    } catch (error) {
      console.error('Failed to update signup:', error);
      alert('Failed to update signup: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancelEdit = () => {
    setEditingSignup(null);
    setEditingSignupData({});
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedSignups.length === 0) {
      alert('Please select signups to update');
      return;
    }

    const selectedCount = selectedSignups.length;

    if (!window.confirm(`Are you sure you want to update ${selectedCount} selected signups to status "${newStatus}"?`)) {
      return;
    }

    try {
      // Update each selected signup
      const updatePromises = selectedSignups.map(signupId =>
        axios.put(`/api/admin/signups/${signupId}`, { status: newStatus })
      );
      
      await Promise.all(updatePromises);
      
      // Update local state
      setSignups(signups.map(signup => 
        selectedSignups.includes(signup.id) 
          ? { ...signup, status: newStatus }
          : signup
      ));
      
      setSelectedSignups([]);
      fetchAdminData(); // Refresh admin stats
      alert(`Successfully updated ${selectedCount} signups to "${newStatus}"!`);
    } catch (error) {
      console.error('Failed to update signups:', error);
      alert('Failed to update signups: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchDiscordEvents = async () => {
    setDiscordEventsLoading(true);
    try {
      const response = await axios.get('/api/admin/discord-events');
      setDiscordEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch Discord events:', error);
      alert('Failed to fetch Discord events: ' + (error.response?.data?.error || error.message));
    } finally {
      setDiscordEventsLoading(false);
    }
  };

  const handleImportDiscordEvent = async (discordEvent) => {
    if (!window.confirm(`Import Discord event "${discordEvent.name}"? This will create a new event and import all interested users as signups.`)) {
      return;
    }

    setImportingEvent(discordEvent.id);
    setImportProgress({ status: 'Importing event...', progress: 0 });

    try {
      const response = await axios.post('/api/admin/import-discord-event', {
        discordEventId: discordEvent.id
      });

      setImportProgress({ status: 'Import completed!', progress: 100 });
      
      // Show detailed success message
      const message = `Successfully imported Discord event "${response.data.eventTitle}"!\n\n` +
        `• ${response.data.signupsImported} signups imported\n` +
        `• ${response.data.usersCreated} new users created\n` +
        `• ${response.data.usersUpdated} existing users updated\n\n` +
        `The event is now available in the app and can be managed like any other event.`;
      
      alert(message);
      
      // Refresh admin data and close modal
      fetchAdminData();
      setShowDiscordImport(false);
    } catch (error) {
      console.error('Failed to import Discord event:', error);
      
      // Handle specific error cases
      if (error.response?.data?.error === 'Discord event already imported') {
        alert(`This Discord event has already been imported as "${error.response.data.eventTitle}". You can find it in the Events management section.`);
      } else {
        alert('Failed to import Discord event: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setImportingEvent(null);
      setImportProgress({});
    }
  };

  const fetchStorytellers = async () => {
    try {
      const response = await axios.get('/api/users/storytellers/list');
      setStorytellers(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch storytellers:', error);
      setStorytellers([]);
      return [];
    }
  };

  const setDefaultStartDate = async () => {
    try {
      // Get the latest event date to determine the next clear week
      const response = await axios.get('/api/admin/events');
      const events = response.data;
      
      if (events.length === 0) {
        // No events exist, start from next Monday
        const today = new Date();
        const nextMonday = new Date(today);
        const daysUntilMonday = (8 - today.getDay()) % 7;
        nextMonday.setDate(today.getDate() + daysUntilMonday);
        
        setTemplateConfig(prev => ({
          ...prev,
          startDate: nextMonday.toISOString().split('T')[0]
        }));
        return;
      }
      
      // Find the latest event date
      const latestEventDate = new Date(Math.max(...events.map(event => new Date(event.date))));
      
      // Add 3 days to ensure we're past the last event
      const nextWeekStart = new Date(latestEventDate);
      nextWeekStart.setDate(latestEventDate.getDate() + 3);
      
      // Find the next Monday
      const daysUntilMonday = (8 - nextWeekStart.getDay()) % 7;
      const nextMonday = new Date(nextWeekStart);
      nextMonday.setDate(nextWeekStart.getDate() + daysUntilMonday);
      
      setTemplateConfig(prev => ({
        ...prev,
        startDate: nextMonday.toISOString().split('T')[0]
      }));
    } catch (error) {
      console.error('Failed to set default start date:', error);
      // Fallback to next Monday
      const today = new Date();
      const nextMonday = new Date(today);
      const daysUntilMonday = (8 - today.getDay()) % 7;
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      
      setTemplateConfig(prev => ({
        ...prev,
        startDate: nextMonday.toISOString().split('T')[0]
      }));
    }
  };

  const handleCreateTemplateEvents = async () => {
    if (!templateConfig.startDate) {
      alert('Please select a start date');
      return;
    }

    const enabledEvents = templateConfig.events.filter(event => event.enabled);
    if (enabledEvents.length === 0) {
      alert('Please enable at least one event day');
      return;
    }

    if (!window.confirm(`Create ${enabledEvents.length} events starting from ${templateConfig.startDate}?`)) {
      return;
    }

    setTemplateEventsLoading(true);
    setImportProgress({ status: 'Creating template events...', progress: 0 });

    try {
      let eventsCreated = 0;
      let discordEventsCreated = 0;
      const errors = [];

      // Use the imported timezone utility

      for (let i = 0; i < enabledEvents.length; i++) {
        const eventConfig = enabledEvents[i];
        
        try {
          // Calculate the event date based on start date and day of week
          const startDateObj = new Date(templateConfig.startDate);
          const targetDay = eventConfig.day; // 0 = Sunday, 1 = Monday, etc.
          const currentDay = startDateObj.getDay();
          
          let daysUntilTarget = targetDay - currentDay;
          if (daysUntilTarget <= 0) {
            daysUntilTarget += 7; // Next week
          }
          
          const eventDate = new Date(startDateObj);
          eventDate.setDate(startDateObj.getDate() + daysUntilTarget);
          
          // Create the event title
          const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
          const eventTitle = `${dayName} Blood on the Clocktower`;
          
          // Convert local time to UTC for storage
          const utcDateTime = userInputToUTC(
            eventDate.toISOString().split('T')[0], 
            eventConfig.time, 
            'America/New_York'
          );
          
                     // Create event data
           const eventData = {
             title: eventTitle,
             description: templateConfig.description,
             date: utcDateTime,
             duration: 180, // 3 hours in minutes
             location: templateConfig.location,
             storytellerIds: eventConfig.storytellerIds,
             createDiscordEvent: templateConfig.createDiscordEvents
           };

          // Create the event using the regular event creation API
          const response = await axios.post('/api/events', eventData);
          
          eventsCreated++;
          
          if (templateConfig.createDiscordEvents) {
            discordEventsCreated++;
          }
          
          // Update progress
          const progress = Math.round(((i + 1) / enabledEvents.length) * 100);
          setImportProgress({ 
            status: `Created ${eventTitle} (${i + 1}/${enabledEvents.length})`, 
            progress 
          });
          
        } catch (eventError) {
          console.error(`Failed to create event for ${eventConfig.name}:`, eventError);
          errors.push(`${eventConfig.name}: ${eventError.response?.data?.error || eventError.message}`);
        }
      }
      
      setImportProgress({ status: 'Template events created!', progress: 100 });
      
      let message = `Successfully created ${eventsCreated} template events!\n\n` +
        `• Events created: ${eventsCreated}\n` +
        `• Discord events created: ${discordEventsCreated}\n\n` +
        `All events are now available in the Events management section.`;
      
      if (errors.length > 0) {
        message += `\n\nErrors:\n${errors.join('\n')}`;
      }
      
      alert(message);
      
      // Refresh admin data and close modal
      fetchAdminData();
      setShowTemplateEvents(false);
      
             // Reset template config
       setTemplateConfig({
         startDate: '',
         storytellerIds: [],
         location: '',
         description: 'The clocktower tolls, shadows stir, and whispers weave through the village…\nJoin us for an evening of deception, deduction, and dramatic executions in Blood on the Clocktower.',
         createDiscordEvents: true,
         events: [
           { day: 1, name: 'Monday', time: '20:30', enabled: false, storytellerIds: [] },
           { day: 2, name: 'Tuesday', time: '20:30', enabled: false, storytellerIds: [] },
           { day: 3, name: 'Wednesday', time: '20:30', enabled: true, storytellerIds: [] },
           { day: 4, name: 'Thursday', time: '20:30', enabled: false, storytellerIds: [] },
           { day: 5, name: 'Friday', time: '20:30', enabled: true, storytellerIds: [] },
           { day: 6, name: 'Saturday', time: '20:30', enabled: true, storytellerIds: [] },
           { day: 0, name: 'Sunday', time: '20:30', enabled: false, storytellerIds: [] },
         ]
       });
    } catch (error) {
      console.error('Failed to create template events:', error);
      alert('Failed to create template events: ' + (error.response?.data?.error || error.message));
    } finally {
      setTemplateEventsLoading(false);
      setImportProgress({});
    }
  };

  const handleTemplateEventToggle = (day) => {
    setTemplateConfig(prev => ({
      ...prev,
      events: prev.events.map(event => 
        event.day === day ? { ...event, enabled: !event.enabled } : event
      )
    }));
  };

  const handleTemplateEventTimeChange = (day, time) => {
    setTemplateConfig(prev => ({
      ...prev,
      events: prev.events.map(event => 
        event.day === day ? { ...event, time } : event
      )
    }));
  };

  const handleStorytellerToggle = (storytellerId) => {
    setTemplateConfig(prev => {
      const newStorytellerIds = prev.storytellerIds.includes(storytellerId)
        ? prev.storytellerIds.filter(id => id !== storytellerId)
        : [...prev.storytellerIds, storytellerId];
      
      // Update all enabled events with the new storyteller selection
      const updatedEvents = prev.events.map(event => ({
        ...event,
        storytellerIds: event.enabled ? newStorytellerIds : event.storytellerIds
      }));
      
      return {
        ...prev,
        storytellerIds: newStorytellerIds,
        events: updatedEvents
      };
    });
  };

  const handleEventStorytellerToggle = (day, storytellerId) => {
    setTemplateConfig(prev => ({
      ...prev,
      events: prev.events.map(event => 
        event.day === day 
          ? {
              ...event,
              storytellerIds: event.storytellerIds.includes(storytellerId)
                ? event.storytellerIds.filter(id => id !== storytellerId)
                : [...event.storytellerIds, storytellerId]
            }
          : event
      )
    }));
  };

  const handleAddStorytellerToEvent = (day) => {
    // Find available storytellers (not already assigned to this event)
    const event = templateConfig.events.find(e => e.day === day);
    const availableStorytellers = storytellers.filter(s => !event.storytellerIds.includes(s.id));
    
    if (availableStorytellers.length === 0) {
      alert('All storytellers are already assigned to this event');
      return;
    }
    
    // Show the dropdown for this day
    setShowStorytellerDropdown(day);
    setStorytellerSearch('');
  };

  const handleStorytellerSearchChange = (value) => {
    setStorytellerSearch(value);
  };

  const getFilteredStorytellers = (day) => {
    const event = templateConfig.events.find(e => e.day === day);
    const availableStorytellers = storytellers.filter(s => !event.storytellerIds.includes(s.id));
    
    if (!storytellerSearch.trim()) {
      return availableStorytellers.slice(0, 3);
    }
    
    return availableStorytellers
      .filter(s => s.username.toLowerCase().includes(storytellerSearch.toLowerCase()))
      .slice(0, 3);
  };

  const handleSelectStoryteller = (day, storytellerId) => {
    handleEventStorytellerToggle(day, storytellerId);
    setShowStorytellerDropdown(null);
    setStorytellerSearch('');
  };

  const handleCloseStorytellerDropdown = () => {
    setShowStorytellerDropdown(null);
    setStorytellerSearch('');
  };



  const getSignupSourceIcon = (source) => {
    switch (source) {
      case 'discord': return <MessageSquareIcon size={14} className="text-purple-500" />;
      case 'webapp': return <ExternalLinkIcon size={14} className="text-blue-500" />;
      case 'botc_command': return <MessageSquareIcon size={14} className="text-green-500" />;
      default: return <ExternalLinkIcon size={14} className="text-gray-500" />;
    }
  };

  const getSignupSourceLabel = (source) => {
    switch (source) {
      case 'discord': return 'Discord';
      case 'webapp': return 'Web App';
      case 'botc_command': return 'Bot Command';
      default: return source;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'creator': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'storyteller': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'player': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      default: return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield size={16} />;
      case 'creator': return <Crown size={16} />;
      case 'storyteller': return <Users size={16} />;
      case 'player': return <UserPlus size={16} />;
      default: return <Users size={16} />;
    }
  };

  const filteredUsers = users.filter(user => {
    const serverName = serverNames[user.discord_id]?.serverName || '';
    const matchesSearch = (user.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.discord_id || '').includes(searchTerm) ||
                         serverName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const filteredEvents = events.filter(event => {
    const matchesSearch = (event.title || '').toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
                         (event.description || '').toLowerCase().includes(eventSearchTerm.toLowerCase());
    const matchesStatus = !eventStatusFilter || event.status === eventStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredSignups = signups.filter(signup => {
    const matchesSearch = (signup.username || '').toLowerCase().includes(signupSearchTerm.toLowerCase()) ||
                         (signup.event_title || '').toLowerCase().includes(signupSearchTerm.toLowerCase());
    const matchesStatus = !signupStatusFilter || signup.status === signupStatusFilter;
    const matchesSource = !signupSourceFilter || signup.signup_source === signupSourceFilter;
    const matchesEvent = !signupEventFilter || signup.event_id === parseInt(signupEventFilter);
    return matchesSearch && matchesStatus && matchesSource && matchesEvent;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center space-x-3">
        <Shield className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Admin Panel</h1>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowUserManagement(true)}>
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{adminData.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowEventManagement(true)}>
          <div className="flex items-center space-x-3">
            <Calendar className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Events</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{adminData.totalEvents}</p>
            </div>
          </div>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowSignupsModal(true)}>
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Signups</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{adminData.totalSignups}</p>
            </div>
          </div>
        </div>
      </div>

      {/* User Management Modal */}
      {showUserManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h2>
              <button
                onClick={() => setShowUserManagement(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users, server names, or Discord IDs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="creator">Creator</option>
                <option value="storyteller">Storyteller</option>
                <option value="player">Player</option>
              </select>
              <button
                onClick={fetchUsers}
                disabled={usersLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {usersLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Refresh Users'}
              </button>
              <button
                onClick={fetchServerNames}
                disabled={serverNamesLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                title="Load server names from Discord"
              >
                {serverNamesLoading ? <Loader className="w-4 h-4 animate-spin" /> : Object.keys(serverNames).length > 0 ? 'Refresh Server Names' : 'Load Server Names'}
              </button>
              <button
                onClick={updatePreferredUsernames}
                disabled={preferredUsernamesLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                title="Update preferred usernames with Discord guild nicknames"
              >
                {preferredUsernamesLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Update Preferred Usernames'}
              </button>
            </div>

            {/* Users Table */}
            {usersLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Server Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Discord ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Signups</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUsers.map((userItem) => (
                      <tr key={userItem.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {userItem.avatar && (
                              <img
                                src={`https://cdn.discordapp.com/avatars/${userItem.discord_id}/${userItem.avatar}.png`}
                                alt={userItem.username}
                                className="h-8 w-8 rounded-full mr-3"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{userItem.username}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{userItem.preferred_username}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {serverNamesLoading ? (
                            <div className="flex items-center">
                              <Loader className="w-4 h-4 animate-spin text-blue-500 mr-2" />
                              <span className="text-sm text-gray-500">Loading...</span>
                            </div>
                          ) : serverNames[userItem.discord_id] ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {serverNames[userItem.discord_id].serverName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {serverNames[userItem.discord_id].nameSource === 'server_nickname' && (
                                  <span className="text-blue-600 dark:text-blue-400">Server Nickname</span>
                                )}
                                {serverNames[userItem.discord_id].nameSource === 'global_display_name' && (
                                  <span className="text-green-600 dark:text-green-400">Global Display Name</span>
                                )}
                                {serverNames[userItem.discord_id].nameSource === 'username' && (
                                  <span className="text-gray-600 dark:text-gray-400">Username</span>
                                )}
                                {serverNames[userItem.discord_id].nameSource === 'effective_display' && (
                                  <span className="text-purple-600 dark:text-purple-400">Effective Display</span>
                                )}
                                {serverNames[userItem.discord_id].nameSource === 'fallback_username' && (
                                  <span className="text-orange-600 dark:text-orange-400">Fallback</span>
                                )}
                              </div>
                              {serverNames[userItem.discord_id].serverNickname && (
                                <div className="text-xs text-blue-600 dark:text-blue-400">
                                  Nickname: {serverNames[userItem.discord_id].serverNickname}
                                </div>
                              )}
                              {serverNames[userItem.discord_id].globalDisplayName && serverNames[userItem.discord_id].globalDisplayName !== serverNames[userItem.discord_id].username && (
                                <div className="text-xs text-green-600 dark:text-green-400">
                                  Global: {serverNames[userItem.discord_id].globalDisplayName}
                                </div>
                              )}
                              {serverNames[userItem.discord_id].error && (
                                <div className="text-xs text-red-600 dark:text-red-400">
                                  Error: {serverNames[userItem.discord_id].error}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              <button
                                onClick={fetchServerNames}
                                disabled={serverNamesLoading}
                                className="text-blue-600 hover:text-blue-800 dark:hover:text-blue-400 underline text-xs"
                                title="Load server names from Discord"
                              >
                                {serverNamesLoading ? 'Loading...' : 'Load Server Names'}
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {userItem.discord_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingUser === userItem.id ? (
                            <select
                              value={userItem.role}
                              onChange={(e) => handleUserRoleUpdate(userItem.id, e.target.value)}
                              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                              <option value="player">Player</option>
                              <option value="storyteller">Storyteller</option>
                              <option value="creator">Creator</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(userItem.role)}`}>
                              {getRoleIcon(userItem.role)}
                              <span className="ml-1 capitalize">{userItem.role}</span>
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="text-xs">
                            <div>Total: {userItem.total_signups}</div>
                            <div className="text-green-600">✓ {userItem.confirmed_signups}</div>
                            <div className="text-yellow-600">? {userItem.tentative_signups}</div>
                            <div className="text-red-600">⚠ {userItem.late_signups}</div>
                            <div className="text-gray-600">✗ {userItem.declined_signups}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(userItem.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingUser === userItem.id ? (
                              <button
                                onClick={() => setEditingUser(null)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              >
                                <X size={16} />
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => setEditingUser(userItem.id)}
                                  className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400"
                                  title="Edit role"
                                >
                                  <Edit size={16} />
                                </button>
                                {userItem.id !== user?.id && (
                                  <button
                                    onClick={() => handleDeleteUser(userItem.id)}
                                    className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                                    title="Delete user"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              Showing {filteredUsers.length} of {users.length} users
            </div>
          </div>
        </div>
      )}

      {/* Event Management Modal */}
      {showEventManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Event Management</h2>
              <button
                onClick={() => setShowEventManagement(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={eventSearchTerm}
                  onChange={(e) => setEventSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full"
                />
              </div>
              <select
                value={eventStatusFilter}
                onChange={(e) => setEventStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={fetchEvents}
                disabled={eventsLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {eventsLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </button>
            </div>

            {/* Events Table */}
            {eventsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Event</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Signups</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedEvents.includes(event.id)}
                              onChange={() => handleSelectEvent(event.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="ml-3">
                              <div className="flex items-center space-x-2">
                                <p className="font-medium text-gray-900 dark:text-gray-100">{event.title}</p>
                                {event.discord_event_id && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                    Discord
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{event.creator_username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(event.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            event.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            event.status === 'completed' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {event.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="text-xs">
                            <div>Total: {event.total_signups}</div>
                            <div className="text-green-600">✓ {event.confirmed_signups}</div>
                            <div className="text-yellow-600">? {event.tentative_signups}</div>
                            <div className="text-red-600">⚠ {event.late_signups}</div>
                            <div className="text-gray-600">✗ {event.declined_signups}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleSelectEvent(event.id)}
                              className={`text-red-600 hover:text-red-900 dark:hover:text-red-400 ${selectedEvents.includes(event.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Select for deletion"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              Showing {filteredEvents.length} of {events.length} events
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={handleSelectAllEvents}
                disabled={events.length === 0 || selectedEvents.length === events.length}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedEvents.length === events.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleDeleteEvents}
                disabled={selectedEvents.length === 0}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {selectedEvents.length === 0 ? 'No Events Selected' : `Delete ${selectedEvents.length} Selected`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signups Modal */}
      {showSignupsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Signups Management</h2>
              <button
                onClick={() => setShowSignupsModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Search and Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search signups..."
                  value={signupSearchTerm}
                  onChange={(e) => setSignupSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full"
                />
              </div>
              <select
                value={signupStatusFilter}
                onChange={(e) => setSignupStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Statuses</option>
                <option value="confirmed">Confirmed</option>
                <option value="tentative">Tentative</option>
                <option value="late">Late</option>
                <option value="declined">Declined</option>
              </select>
              <select
                value={signupSourceFilter}
                onChange={(e) => setSignupSourceFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Sources</option>
                <option value="webapp">Web App</option>
                <option value="discord">Discord</option>
                <option value="botc_command">Bot Command</option>
              </select>
              <select
                value={signupEventFilter}
                onChange={(e) => setSignupEventFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Events</option>
                {availableEvents.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={fetchSignups}
                  disabled={signupsLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {signupsLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </button>
                <button
                  onClick={handleSelectAllSignups}
                  disabled={filteredSignups.length === 0}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {selectedSignups.length === filteredSignups.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedSignups.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Bulk Actions:</span>
                    <select
                      onChange={(e) => handleBulkStatusUpdate(e.target.value)}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      defaultValue=""
                    >
                      <option value="" disabled>Update Status...</option>
                      <option value="confirmed">Set to Confirmed</option>
                      <option value="tentative">Set to Tentative</option>
                      <option value="late">Set to Late</option>
                      <option value="declined">Set to Declined</option>
                    </select>
                    <button
                      onClick={handleDeleteSelectedSignups}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Delete {selectedSignups.length} Selected
                    </button>
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredSignups.length} of {signups.length} signups
                {selectedSignups.length > 0 && (
                  <span className="ml-2 text-blue-600 dark:text-blue-400">
                    ({selectedSignups.length} selected)
                  </span>
                )}
              </div>
            </div>

            {/* Signups Table */}
            {signupsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedSignups.length === filteredSignups.length && filteredSignups.length > 0}
                          onChange={handleSelectAllSignups}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Event</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Notes</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredSignups.map((signup) => (
                      <tr key={signup.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedSignups.includes(signup.id)}
                            onChange={() => handleSelectSignup(signup.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {signup.avatar && (
                              <img
                                src={`https://cdn.discordapp.com/avatars/${signup.discord_id}/${signup.avatar}.png`}
                                alt={signup.username}
                                className="h-8 w-8 rounded-full mr-3"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{signup.username}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">{signup.event_title}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(signup.event_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingSignup === signup.id ? (
                            <select
                              value={editingSignupData.status}
                              onChange={(e) => setEditingSignupData(prev => ({ ...prev, status: e.target.value }))}
                              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                              <option value="confirmed">Confirmed</option>
                              <option value="tentative">Tentative</option>
                              <option value="late">Late</option>
                              <option value="declined">Declined</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              signup.status === 'confirmed' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                              signup.status === 'tentative' ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                              signup.status === 'late' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                              'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                            }`}>
                              {signup.status}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getSignupSourceIcon(signup.signup_source)}
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {getSignupSourceLabel(signup.signup_source)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(signup.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {editingSignup === signup.id ? (
                            <textarea
                              value={editingSignupData.notes}
                              onChange={(e) => setEditingSignupData(prev => ({ ...prev, notes: e.target.value }))}
                              rows={2}
                              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full"
                              placeholder="Add notes..."
                            />
                          ) : (
                            signup.notes ? (
                              <div className="max-w-xs truncate" title={signup.notes}>
                                {signup.notes}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {editingSignup === signup.id ? (
                              <>
                                <button
                                  onClick={() => handleUpdateSignup(signup.id)}
                                  className="text-green-600 hover:text-green-900 dark:hover:text-green-400"
                                  title="Save changes"
                                >
                                  <CheckCircle size={16} />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  title="Cancel edit"
                                >
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleEditSignup(signup)}
                                className="text-blue-600 hover:text-blue-900 dark:hover:text-blue-400"
                                title="Edit signup"
                              >
                                <Edit size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

             {/* Template Events */}
       <div className="card">
         <div className="flex items-center justify-between mb-6">
           <div className="flex items-center space-x-3">
             <Calendar className="w-6 h-6 text-green-500" />
             <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Template Events</h2>
           </div>
           <button
             onClick={() => setShowTemplateEvents(true)}
             className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
           >
             <Plus size={16} />
             <span>Create Template Events</span>
           </button>
         </div>
         
         <div className="text-sm text-gray-600 dark:text-gray-400">
           <p>Create a week's worth of events with one click using customizable templates.</p>
           <p className="mt-1">Perfect for setting up regular game nights with consistent settings and storytellers.</p>
         </div>
       </div>

             {/* Discord Import */}
       <div className="card">
         <div className="flex items-center justify-between mb-6">
           <div className="flex items-center space-x-3">
             <Download className="w-6 h-6 text-purple-500" />
             <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Discord Import</h2>
           </div>
           <button
             onClick={() => setShowDiscordImport(true)}
             className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
           >
             <ExternalLink size={16} />
             <span>Import Discord Events</span>
           </button>
         </div>
         
         <div className="text-sm text-gray-600 dark:text-gray-400">
           <p>Import existing Discord guild scheduled events and their interested users as signups.</p>
           <p className="mt-1">This is useful for events that weren't created through the app but have existing Discord interest.</p>
         </div>
       </div>

       {/* Site Settings */}
       <div className="card">
         <div className="flex items-center space-x-3 mb-6">
           <Settings className="w-6 h-6 text-primary" />
           <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Site Settings</h2>
         </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Events Page Banner
            </label>
            <textarea
              value={editingSettings.events_banner || ''}
              onChange={(e) => setEditingSettings(prev => ({ ...prev, events_banner: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter banner message for the events page..."
            />
            <button
              onClick={() => handleSettingUpdate('events_banner', editingSettings.events_banner)}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Update Banner
            </button>
          </div>



          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Discord Event Image
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Upload an image to be used as the cover image for Discord guild scheduled events. 
              Supported formats: JPG, PNG, GIF. Recommended size: 960x540 pixels.
            </p>
            
            {/* Image Preview */}
            {editingSettings.discord_event_image && (
              <div className="mb-3">
                <img
                  src={editingSettings.discord_event_image}
                  alt="Discord event preview"
                  className="max-w-xs rounded-lg border border-gray-300 dark:border-gray-600"
                />
                <button
                  onClick={() => {
                    setEditingSettings(prev => ({ ...prev, discord_event_image: '' }));
                    handleSettingUpdate('discord_event_image', '');
                  }}
                  className="mt-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Remove Image
                </button>
              </div>
            )}
            
            {/* File Upload */}
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Validate file size (max 10MB)
                  if (file.size > 10 * 1024 * 1024) {
                    alert('Image file size must be less than 10MB');
                    e.target.value = '';
                    return;
                  }
                  
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    const dataUri = event.target.result;
                    // Validate that it's a proper data URI
                    if (dataUri.startsWith('data:image/')) {
                      setEditingSettings(prev => ({ ...prev, discord_event_image: dataUri }));
                    } else {
                      alert('Invalid image file. Please select a valid JPG, PNG, or GIF image.');
                      e.target.value = '';
                    }
                  };
                  reader.onerror = () => {
                    alert('Error reading image file. Please try again.');
                    e.target.value = '';
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            {editingSettings.discord_event_image && editingSettings.discord_event_image !== siteSettings.discord_event_image && (
              <button
                onClick={() => handleSettingUpdate('discord_event_image', editingSettings.discord_event_image)}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update Discord Event Image
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Events</h3>
          <div className="space-y-3">
            {adminData.recentEvents?.map((event) => (
              <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{event.title}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{new Date(event.date).toLocaleDateString()}</p>
                </div>
                <span className="text-sm text-gray-500">{event.signup_count} signups</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Users</h3>
          <div className="space-y-3">
            {adminData.recentUsers?.map((user) => (
              <div key={user.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                {user.avatar && (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                    alt={user.username}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{user.username}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{user.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Test Environment */}
      {ENABLE_TEST_ENVIRONMENT && TestEnvironment && (
        <TestEnvironment />
      )}

      {/* Template Events Modal */}
      {showTemplateEvents && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Template Events</h2>
              <button
                onClick={() => setShowTemplateEvents(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Progress */}
            {importProgress.status && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Loader className="w-5 h-5 animate-spin text-green-600" />
                  <div>
                    <p className="text-green-800 dark:text-green-200 font-medium">{importProgress.status}</p>
                    {importProgress.progress > 0 && (
                      <div className="w-full bg-green-200 dark:bg-green-800 rounded-full h-2 mt-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${importProgress.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Basic Settings */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={templateConfig.startDate}
                      onChange={(e) => setTemplateConfig(prev => ({ ...prev, startDate: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                      className="input w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={templateConfig.location}
                      onChange={(e) => setTemplateConfig(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Discord Voice Channel #general"
                      className="input w-full"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={templateConfig.description}
                    onChange={(e) => setTemplateConfig(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Description for all events..."
                    className="input w-full"
                  />
                </div>
              </div>

              {/* Week View */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Week View</h3>
                
                {/* Week Preview */}
                {templateConfig.startDate && (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                      Week of {new Date(templateConfig.startDate).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                )}
                
                {/* Week Grid */}
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <div key={day} className="text-center text-xs font-medium text-gray-600 dark:text-gray-400 py-2">
                      {day}
                    </div>
                  ))}
                  
                                     {templateConfig.events.map((event) => {
                     const eventDate = templateConfig.startDate ? 
                       (() => {
                         const startDate = new Date(templateConfig.startDate);
                         // startDate is already a Monday, so we just need to add the day offset
                         // Monday = 1, Tuesday = 2, ..., Sunday = 0
                         let daysToAdd = event.day;
                         if (event.day === 0) daysToAdd = 6; // Sunday becomes day 6
                         else daysToAdd = event.day - 1; // Monday becomes day 0, Tuesday becomes day 1, etc.
                         
                         const targetDate = new Date(startDate);
                         targetDate.setDate(startDate.getDate() + daysToAdd);
                         return targetDate;
                       })() : null;
                    
                    return (
                      <div 
                        key={event.day} 
                        className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          event.enabled 
                            ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700' 
                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                        }`}
                        onClick={() => handleTemplateEventToggle(event.day)}
                      >
                        <div className="text-center">
                          <div className={`text-sm font-medium ${
                            event.enabled 
                              ? 'text-green-800 dark:text-green-200' 
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {event.name.slice(0, 3)}
                          </div>
                          {eventDate && (
                            <div className={`text-xs ${
                              event.enabled 
                                ? 'text-green-600 dark:text-green-300' 
                                : 'text-gray-500 dark:text-gray-500'
                            }`}>
                              {eventDate.getDate()}
                            </div>
                          )}
                          <div className={`text-xs mt-1 ${
                            event.enabled 
                              ? 'text-green-600 dark:text-green-300' 
                              : 'text-gray-500 dark:text-gray-500'
                          }`}>
                            {event.time}
                          </div>
                        </div>
                        
                        {event.enabled && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                                 {/* Individual Day Settings */}
                 <div className="space-y-3">
                   <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Day Settings</h4>
                   {templateConfig.events.filter(event => event.enabled).map((event) => (
                     <div key={event.day} className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                       <div className="flex items-center justify-between mb-3">
                         <span className="text-sm font-medium text-green-800 dark:text-green-200">
                           {event.name}
                         </span>
                         <div className="flex items-center space-x-2">
                           <label className="text-sm text-gray-600 dark:text-gray-400">Time:</label>
                           <input
                             type="time"
                             value={event.time}
                             onChange={(e) => handleTemplateEventTimeChange(event.day, e.target.value)}
                             className="input text-sm"
                           />
                         </div>
                       </div>
                       
                                               {/* Storytellers for this event */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Storytellers:</span>
                            <button
                              onClick={() => handleAddStorytellerToEvent(event.day)}
                              className="flex items-center space-x-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                            >
                              <Plus size={12} />
                              <span>Add</span>
                            </button>
                          </div>
                          
                          {/* Storyteller Search Dropdown */}
                          {showStorytellerDropdown === event.day && (
                            <div className="relative storyteller-dropdown">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={storytellerSearch}
                                  onChange={(e) => handleStorytellerSearchChange(e.target.value)}
                                  placeholder="Search storytellers..."
                                  className="flex-1 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  autoFocus
                                />
                                <button
                                  onClick={handleCloseStorytellerDropdown}
                                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                              
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                                {getFilteredStorytellers(event.day).map((storyteller) => (
                                  <button
                                    key={storyteller.id}
                                    onClick={() => handleSelectStoryteller(event.day, storyteller.id)}
                                    className="w-full flex items-center space-x-2 px-3 py-2 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                  >
                                    {storyteller.avatar && (
                                      <img
                                        src={`https://cdn.discordapp.com/avatars/${storyteller.discord_id}/${storyteller.avatar}.png`}
                                        alt={storyteller.username}
                                        className="w-4 h-4 rounded-full"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    )}
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {storyteller.username}
                                    </span>
                                  </button>
                                ))}
                                {getFilteredStorytellers(event.day).length === 0 && (
                                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                    No storytellers found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {event.storytellerIds.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {event.storytellerIds.map((storytellerId) => {
                                const storyteller = storytellers.find(s => s.id === storytellerId);
                                return storyteller ? (
                                  <div key={storytellerId} className="flex items-center space-x-2 bg-white dark:bg-gray-700 px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-600">
                                    {storyteller.avatar && (
                                      <img
                                        src={`https://cdn.discordapp.com/avatars/${storyteller.discord_id}/${storyteller.avatar}.png`}
                                        alt={storyteller.username}
                                        className="w-4 h-4 rounded-full"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    )}
                                    <span className="text-xs text-gray-700 dark:text-gray-300">
                                      {storyteller.username}
                                    </span>
                                    <button
                                      onClick={() => handleEventStorytellerToggle(event.day, storytellerId)}
                                      className="text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                    >
                                      <X size={12} />
                                    </button>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                              No storytellers assigned
                            </div>
                          )}
                        </div>
                     </div>
                   ))}
                 </div>
              </div>

              {/* Global Storytellers */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Global Storytellers</h3>
                                 <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                   Select storytellers to assign to all enabled events. You can then customize individual events in the Day Settings section above.
                 </p>
                
                <div className="space-y-3">
                  {storytellers.map((storyteller) => (
                    <label key={storyteller.id} className="flex items-center space-x-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <input
                        type="checkbox"
                        checked={templateConfig.storytellerIds.includes(storyteller.id)}
                        onChange={() => handleStorytellerToggle(storyteller.id)}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex items-center space-x-3 flex-1">
                        {storyteller.avatar && (
                          <img
                            src={`https://cdn.discordapp.com/avatars/${storyteller.discord_id}/${storyteller.avatar}.png`}
                            alt={storyteller.username}
                            className="w-8 h-8 rounded-full"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {storyteller.username}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {storyteller.role}
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              

              {/* Discord Integration */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Discord Integration</h3>
                
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={templateConfig.createDiscordEvents}
                    onChange={(e) => setTemplateConfig(prev => ({ ...prev, createDiscordEvents: e.target.checked }))}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Create Discord Events
                  </label>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  When enabled, this will automatically create Discord events for each template event.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowTemplateEvents(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplateEvents}
                  disabled={templateEventsLoading}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>{templateEventsLoading ? 'Creating...' : 'Create Template Events'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discord Import Modal */}
      {showDiscordImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Import Discord Events</h2>
              <button
                onClick={() => setShowDiscordImport(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Import Progress */}
            {importProgress.status && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Loader className="w-5 h-5 animate-spin text-blue-600" />
                  <div>
                    <p className="text-blue-800 dark:text-blue-200 font-medium">{importProgress.status}</p>
                    {importProgress.progress > 0 && (
                      <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mt-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${importProgress.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Discord Events Table */}
            {discordEventsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Event</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Interested</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {discordEvents.map((discordEvent) => (
                      <tr key={discordEvent.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{discordEvent.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={discordEvent.description}>{discordEvent.description}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(discordEvent.scheduled_start_time).toLocaleDateString()}
                          <br />
                          <span className="text-xs">{new Date(discordEvent.scheduled_start_time).toLocaleTimeString()}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            discordEvent.status === 1 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            discordEvent.status === 2 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {discordEvent.status === 1 ? 'Active' : 
                             discordEvent.status === 2 ? 'Completed' : 'Cancelled'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="text-center">
                            <div className="text-lg font-semibold text-purple-600">{discordEvent.interested_count || 0}</div>
                            <div className="text-xs text-gray-400">users interested</div>
                          </div>
                        </td>
                                                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                           {discordEvent.already_imported ? (
                             <div className="flex items-center space-x-2">
                               <CheckCircle className="w-4 h-4 text-green-500" />
                               <span className="text-green-600 text-sm">Already Imported</span>
                             </div>
                           ) : (
                             <button
                               onClick={() => handleImportDiscordEvent(discordEvent)}
                               disabled={importingEvent === discordEvent.id || discordEvent.status !== 1}
                               className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                                 importingEvent === discordEvent.id
                                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                   : discordEvent.status !== 1
                                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                   : 'bg-purple-600 text-white hover:bg-purple-700'
                               }`}
                               title={discordEvent.status !== 1 ? 'Only active events can be imported' : 'Import this Discord event'}
                             >
                               {importingEvent === discordEvent.id ? (
                                 <Loader className="w-4 h-4 animate-spin" />
                               ) : (
                                 'Import'
                               )}
                             </button>
                           )}
                         </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {discordEvents.length === 0 && !discordEventsLoading && (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No Discord events found</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Make sure your Discord bot has the necessary permissions</p>
              </div>
            )}

            <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              Showing {discordEvents.length} Discord events
            </div>
          </div>
        </div>
      )}

      {/* Delete Events Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete Events
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete {selectedEvents.length} selected events? This action cannot be undone.
              </p>
              
              {/* Check if any selected events have Discord events */}
              {(() => {
                const selectedEventData = events.filter(event => selectedEvents.includes(event.id));
                const eventsWithDiscord = selectedEventData.filter(event => event.discord_event_id);
                
                if (eventsWithDiscord.length > 0) {
                  return (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="deleteDiscordEvents"
                          checked={deleteDiscordEvents}
                          onChange={(e) => setDeleteDiscordEvents(e.target.checked)}
                          className="mt-1"
                        />
                        <div>
                          <label htmlFor="deleteDiscordEvents" className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            Also delete Discord events ({eventsWithDiscord.length} events have Discord events)
                          </label>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            This will remove the associated Discord events from your server as well.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            No Discord events associated
                          </p>
                          <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
                            None of the selected events are linked to Discord events, so no Discord deletion is needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteEvents}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Events
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Admin; 